import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import {
  type LoadoutSlot,
  loadLoadout,
  saveLoadout,
  loadoutKeyFor,
  defaultLoadout,
} from '../../systems/loadout';
import styles from './LoadoutEditor.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// LoadoutEditor — monta a "barra de batalha" (8 slots) FORA do combate.
// Usado como aba na Ficha do Personagem. A escolha vale para a próxima
// batalha (localStorage por classe) — a CommandBar lê a MESMA chave.
// ─────────────────────────────────────────────────────────────────────────────

interface SpellOpt { id: string; name: string; ap_cost?: number; icon_url?: string | null }
interface ConsumOpt { item: { id: string; name: string; icon_url?: string | null }; quantity: number }

export default function LoadoutEditor({ token }: { token: string }) {
  const { t } = useTranslation();
  const [spells, setSpells] = useState<SpellOpt[]>([]);
  const [consumables, setConsumables] = useState<ConsumOpt[]>([]);
  const [loadoutKey, setLoadoutKey] = useState<string>(loadoutKeyFor(null));
  const [slots, setSlots] = useState<LoadoutSlot[]>(() => loadLoadout(loadoutKeyFor(null)));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/user/loadout/options', {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `HTTP ${res.status}`);
        const data = await res.json();
        setSpells(data.spells ?? []);
        setConsumables(data.consumables ?? []);
        const key = loadoutKeyFor(data.class);
        setLoadoutKey(key);
        const saved = loadLoadout(key);
        setSlots(saved.some(s => s !== null)
          ? saved
          : defaultLoadout((data.spells ?? []).map((s: SpellOpt) => s.id), (data.consumables ?? []).map((c: ConsumOpt) => c.item.id)));
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const spellById = useMemo(() => new Map(spells.map(s => [s.id, s])), [spells]);
  const itemById = useMemo(() => new Map(consumables.map(c => [c.item.id, c])), [consumables]);

  const commit = (next: LoadoutSlot[]) => { setSlots(next); saveLoadout(loadoutKey, next); };
  const removeSlot = (idx: number) => { const n = slots.slice(); n[idx] = null; commit(n); };
  const toggle = (slot: NonNullable<LoadoutSlot>) => {
    const ex = slots.findIndex(s => s && s.kind === slot.kind && s.id === slot.id);
    if (ex >= 0) { removeSlot(ex); return; }
    const empty = slots.findIndex(s => s === null);
    if (empty < 0) return;
    const n = slots.slice(); n[empty] = slot; commit(n);
  };
  const reset = () => commit(defaultLoadout(spells.map(s => s.id), consumables.map(c => c.item.id)));

  const spellName = (id: string) => t(`spell.${id}.name` as any) || spellById.get(id)?.name || '?';
  const slotLabel = (slot: LoadoutSlot) =>
    !slot ? '' : slot.kind === 'spell' ? spellName(slot.id) : (itemById.get(slot.id)?.item.name ?? '?');
  const slotIcon = (slot: LoadoutSlot): string | null | undefined =>
    !slot ? undefined : slot.kind === 'spell' ? spellById.get(slot.id)?.icon_url : itemById.get(slot.id)?.item.icon_url;

  if (loading) return <div className={styles.center}><div className={styles.spinner} /></div>;
  if (error) return <div className={styles.errorBox}>{error}</div>;

  return (
    <div className={styles.wrap}>
      <p className={styles.hint}>{t('command.configHint' as any)}</p>

      <div className={styles.slotRow}>
        {slots.map((slot, idx) => {
          const icon = slotIcon(slot);
          return (
            <button
              key={idx}
              className={`${styles.slot} ${slot ? '' : styles.slotEmpty}`}
              onClick={() => slot && removeSlot(idx)}
              title={slot ? `${slotLabel(slot)} — ${t('command.removeSlot' as any) || 'clique para remover'}` : ''}
            >
              <span className={styles.slotNum}>{idx + 1}</span>
              <span className={styles.slotIcon}>
                {slot
                  ? (icon
                      ? <img src={icon} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      : <b>{slotLabel(slot).charAt(0).toUpperCase()}</b>)
                  : <span className={styles.slotDash}>+</span>}
              </span>
              <span className={styles.slotLabel}>{slotLabel(slot) || '—'}</span>
            </button>
          );
        })}
      </div>

      <div className={styles.group}>
        <span className={styles.groupTitle}>{t('command.spells' as any) || 'Feitiços'}</span>
        <div className={styles.palette}>
          {spells.map(s => {
            const inBar = slots.some(sl => sl && sl.kind === 'spell' && sl.id === s.id);
            return (
              <button key={s.id} className={`${styles.chip} ${inBar ? styles.chipOn : ''}`} onClick={() => toggle({ kind: 'spell', id: s.id })}>
                {s.icon_url && <img className={styles.chipIcon} src={s.icon_url} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                {spellName(s.id)}{s.ap_cost != null && <small> {s.ap_cost} AP</small>}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.group}>
        <span className={styles.groupTitle}>{t('command.items' as any) || 'Itens'}</span>
        <div className={styles.palette}>
          {consumables.length === 0 && <span className={styles.noItems}>{t('command.noItems' as any) || 'Nenhum item usável.'}</span>}
          {consumables.map(c => {
            const inBar = slots.some(sl => sl && sl.kind === 'item' && sl.id === c.item.id);
            return (
              <button key={c.item.id} className={`${styles.chip} ${inBar ? styles.chipOn : ''}`} onClick={() => toggle({ kind: 'item', id: c.item.id })}>
                {c.item.icon_url && <img className={styles.chipIcon} src={c.item.icon_url} alt="" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                {c.item.name} <small>×{c.quantity}</small>
              </button>
            );
          })}
        </div>
      </div>

      <button className={styles.resetBtn} onClick={reset}>{t('command.reset' as any) || 'Restaurar padrão'}</button>
    </div>
  );
}
